import { useNavigate } from '@tanstack/react-router'
import { FormDialog } from '@trellis/roots/overlays'
import { useState } from 'react'
import { toast } from 'sonner'

import { InvoiceNumberField } from '@/components/invoice-number-field'
import {
  useDuplicateInvoiceMutation,
  useNextInvoiceNumberQuery,
} from '@/hooks/useInvoices'

interface DuplicateInvoiceDialogProps {
  invoice: { id: string; invoiceNumber: string } | null
  onOpenChange: (open: boolean) => void
}

/**
 * Confirmation step for duplicating an invoice — accepting the prefilled next number is
 * functionally identical to the previous one-click duplicate, plus this one confirm
 * step.
 *
 * @see specs/README.md → invoices/ → "Reserve Historical Invoice Numbers on Import"
 */
export function DuplicateInvoiceDialog({
  invoice,
  onOpenChange,
}: DuplicateInvoiceDialogProps) {
  const navigate = useNavigate()
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [blocked, setBlocked] = useState(false)

  const open = !!invoice
  const { data: nextInvoiceNumberResult } = useNextInvoiceNumberQuery(open)
  const duplicateMutation = useDuplicateInvoiceMutation()

  function handleReset() {
    setInvoiceNumber('')
    setBlocked(false)
  }

  function handleConfirm() {
    if (!invoice || blocked) return
    duplicateMutation.mutate(
      {
        id: invoice.id,
        data: { invoiceNumber: invoiceNumber || undefined },
      },
      {
        onSuccess: (result) => {
          onOpenChange(false)
          toast.success('Invoice duplicated', {
            description: "You're now editing the copy.",
          })
          void navigate({ to: '/invoices/$id/edit', params: { id: result.data.id } })
        },
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error
              ? caughtError.message
              : 'Failed to duplicate invoice'
          toast.error('Failed to duplicate invoice', { description: message })
        },
      },
    )
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Duplicate invoice"
      description={`Creates a new DRAFT copy of ${invoice?.invoiceNumber}.`}
      submitLabel="Duplicate"
      pendingLabel="Duplicating..."
      isPending={duplicateMutation.isPending}
      submitDisabled={blocked}
      onSubmit={handleConfirm}
      onReset={handleReset}
    >
      <InvoiceNumberField
        id="duplicateInvoiceNumber"
        value={invoiceNumber}
        onChange={setInvoiceNumber}
        prefillValue={nextInvoiceNumberResult?.data?.invoiceNumber}
        disclaimer="New invoice references will always continue from the highest reference in use — including any custom reference you set here."
        onAvailabilityChange={setBlocked}
      />
    </FormDialog>
  )
}
